import { Theme, TenantTag } from '@logto/schemas';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import CreateTenantHeaderIconDark from '@/assets/icons/create-tenant-header-dark.svg?react';
import CreateTenantHeaderIcon from '@/assets/icons/create-tenant-header.svg?react';
import { type TenantResponse } from '@/cloud/types/router';
import { buildSelfHostedTenantResponse } from '@/consts';
import Button from '@/ds-components/Button';
import FormField from '@/ds-components/FormField';
import ModalLayout from '@/ds-components/ModalLayout';
import RadioGroup, { Radio } from '@/ds-components/RadioGroup';
import TextInput from '@/ds-components/TextInput';
import useTenantApi from '@/hooks/use-tenant-api';
import useTheme from '@/hooks/use-theme';
import modalStyles from '@/scss/modal.module.scss';
import { trySubmitSafe } from '@/utils/form';

import EnvTagOptionContent from './EnvTagOptionContent';
import styles from './index.module.scss';

type Props = {
  readonly isOpen: boolean;
  readonly onClose: (tenant?: TenantResponse) => void;
};

type FormValues = {
  name: string;
  tag: TenantTag;
};

const defaultValues: FormValues = {
  name: '',
  tag: TenantTag.Development,
};

export default function SelfHostedCreateTenantModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'admin_console' });
  const theme = useTheme();
  const tenantApi = useTenantApi();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  const onCreateClick = handleSubmit(
    trySubmitSafe(async ({ name, tag }) => {
      const created = await tenantApi.create({ name, tag });
      // The self-hosted tenant response only carries the minimal core metadata; the shape-adapter
      // fills in Cloud-only fields (subscription, usage, quota…) so every downstream consumer
      // (tenant selector, status tag, paywalls) can treat it uniformly.
      onClose(buildSelfHostedTenantResponse(created));
      toast.success(t('tenants.create_modal.tenant_created'));
    })
  );

  return (
    <Modal
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      isOpen={isOpen}
      className={modalStyles.content}
      overlayClassName={modalStyles.overlay}
      onAfterClose={() => {
        reset(defaultValues);
      }}
      onRequestClose={() => {
        onClose();
      }}
    >
      <ModalLayout
        title="tenants.create_modal.title"
        subtitle="tenants.create_modal.subtitle"
        headerIcon={
          theme === Theme.Light ? <CreateTenantHeaderIcon /> : <CreateTenantHeaderIconDark />
        }
        footer={
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting}
            htmlType="submit"
            title="tenants.create_modal.create_button"
            size="large"
            type="primary"
            onClick={onCreateClick}
          />
        }
        size="large"
        onClose={onClose}
      >
        <FormField isRequired title="tenants.settings.tenant_name">
          <TextInput
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            {...register('name', { required: true })}
            error={Boolean(errors.name)}
            disabled={isSubmitting}
          />
        </FormField>

        <FormField title="tenants.create_modal.tenant_usage_purpose">
          <Controller
            control={control}
            name="tag"
            rules={{ required: true }}
            render={({ field: { onChange, value, name } }) => (
              <RadioGroup
                type="card"
                className={styles.envTagRadioGroup}
                value={value}
                name={name}
                onChange={onChange}
              >
                <Radio value={TenantTag.Development}>
                  <EnvTagOptionContent isPrivateRegion tag={TenantTag.Development} />
                </Radio>
                <Radio value={TenantTag.Production}>
                  <EnvTagOptionContent isPrivateRegion tag={TenantTag.Production} />
                </Radio>
              </RadioGroup>
            )}
          />
        </FormField>
      </ModalLayout>
    </Modal>
  );
}
